///
/// EarthBrowser
///
/// Created by Matt Giger on 9/24/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Shared mount path for image ground overlays: decode (and downsample)
/// the picture, park the bitmap for the engine, and open the floating
/// properties window. Used by the file-drop handler (camera-centered
/// default bounds), cloud dataset opens, dataset share links, and story
/// playback (bounds provided by the source).
///

import { registerGroundOverlayImage } from "@/earthbrowser/dataset-distribution/ground-overlay/groundOverlayImages"
import { isMobile } from "@/earthbrowser/utils/platform"
import { wgs } from "@/earthbrowser/utils/geographic"
import type { CloudDatasetLink, GeoBounds } from "@/redux/dialogSlice"
import { bringDatasetWindowToFront, openDatasetWindow } from "@/redux/dialogSlice"
import type { AppDispatch, RootState } from "@/redux/store"
import { cameraLatLonDeg } from "@/utils/savedViews"
import { createWindowState, generateWindowId } from "@/utils/windowUtils"

/** Default overlay opacity for freshly imported images */
export const cDefaultOverlayOpacity = 0.9

export interface ImageOverlayMountOptions {
	/** Placement; omitted = camera-centered rect preserving image aspect */
	bounds?: GeoBounds
	/** 0..1 (default 0.9) */
	opacity?: number
	/** Cloud identity when opened from a cloud source */
	cloudDataset?: CloudDatasetLink
	/** Focus the new window (default true; false during story playback) */
	bringToFront?: boolean
}

/**
 * Decode an image file and open its ground-overlay window. Returns the
 * window id, or null when a window for this filename is already open or
 * the file can't be decoded.
 */
export async function mountImageOverlay(
	dispatch: AppDispatch,
	getState: () => RootState,
	file: File,
	options?: ImageOverlayMountOptions,
): Promise<string | null> {
	const state = getState()
	const existing = Object.values(state.dialog.datasetWindows).find(
		w => w.title === file.name && w.windowType === "image"
	)
	if (existing) {
		console.log(`Window already exists for ${file.name}, skipping`)
		return null
	}

	// Decode, downsampling to the GPU-friendly cap (mipmapped RGBA on top
	// of the weather overlays; a full 8K scan would blow mobile budgets)
	let bitmap: ImageBitmap
	let naturalWidth: number
	let naturalHeight: number
	try {
		const full = await createImageBitmap(file)
		naturalWidth = full.width
		naturalHeight = full.height
		const maxDim = isMobile() ? 2048 : 4096
		if (full.width > maxDim || full.height > maxDim) {
			const scale = maxDim / Math.max(full.width, full.height)
			bitmap = await createImageBitmap(full, {
				resizeWidth: Math.max(1, Math.round(full.width * scale)),
				resizeHeight: Math.max(1, Math.round(full.height * scale)),
				resizeQuality: "high",
			})
			full.close()
		} else {
			bitmap = full
		}
	} catch (error) {
		console.error(`Could not decode image ${file.name}:`, error)
		return null
	}

	const bounds = options?.bounds ?? defaultBoundsFromCamera(state, naturalWidth / naturalHeight)
	const thumbnail = makeThumbnail(bitmap)

	const windowId = generateWindowId("image")
	registerGroundOverlayImage(windowId, { bitmap, naturalWidth, naturalHeight })

	dispatch(openDatasetWindow({
		...createWindowState(windowId, file.name, { width: 340, height: 520 }),
		windowType: "image",
		dockMode: "window-only",
		imageOverlay: {
			bounds,
			opacity: options?.opacity ?? cDefaultOverlayOpacity,
			naturalWidth,
			naturalHeight,
		},
		plotImageData: thumbnail,
		cloudDataset: options?.cloudDataset,
	}))
	if (options?.bringToFront ?? true) {
		dispatch(bringDatasetWindowToFront(windowId))
	}
	return windowId
}

/**
 * A rectangle centered on the camera's ground point, sized to roughly
 * fill the view at the current altitude and preserving the image aspect.
 */
function defaultBoundsFromCamera(state: RootState, aspect: number): GeoBounds {
	const camera = state.earthbrowser.cameraState
	const { lat, lon } = cameraLatLonDeg(camera.theta, camera.phi)
	const altitudeKm = Math.max(1, camera.radius - wgs.a)

	// Visible ground span ≈ altitude (60° fov); 1° latitude ≈ 111 km
	const halfLat = Math.min(30, Math.max(0.2, (altitudeKm / 111) * 0.4))
	const safeAspect = isFinite(aspect) && aspect > 0 ? Math.min(8, Math.max(0.125, aspect)) : 1
	const cosLat = Math.max(0.2, Math.cos(lat * Math.PI / 180))
	const halfLon = Math.min(90, halfLat * safeAspect / cosLat)

	const north = Math.min(85, lat + halfLat)
	const south = Math.max(-85, lat - halfLat)
	const west = Math.max(-180, lon - halfLon)
	const east = Math.min(180, lon + halfLon)
	return {
		west: round4(west),
		south: round4(south),
		east: round4(east),
		north: round4(north),
	}
}

function round4(value: number): number {
	return Math.round(value * 10000) / 10000
}

/** Small JPEG preview for the properties panel (serializable in redux) */
function makeThumbnail(bitmap: ImageBitmap): string | undefined {
	try {
		const maxWidth = 280
		const scale = Math.min(1, maxWidth / bitmap.width)
		const canvas = document.createElement("canvas")
		canvas.width = Math.max(1, Math.round(bitmap.width * scale))
		canvas.height = Math.max(1, Math.round(bitmap.height * scale))
		const ctx = canvas.getContext("2d")
		if (!ctx) return undefined
		ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height)
		return canvas.toDataURL("image/jpeg", 0.75)
	} catch {
		return undefined
	}
}
